import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@constants/routes';
import { CRITICAL_TOAST_THROTTLE_MS } from '@constants/timing';
import { SHOWN } from '@constants/limits';
import { ALERT_TOAST as T, ALERT_TYPE_TEXT } from '@locales/id/alerts';
import type { AlertEvent } from '@lib/api';
import { num } from '@lib/format';
import { alertSoundOn } from '@lib/hooks';
import { useStream } from '@lib/stream';
import { playAlertTone } from '@lib/utils/alertTone';
import { useToast } from '@ui';

/**
 * A toast with "Lihat" (the alert's drawer) for every new critical alert that needs action. Within CRITICAL_TOAST_THROTTLE_MS of the last toast,
 * alerts wait and come together as one "N peringatan kritis baru" toast when the window ends (none is dropped).
 * With the alert tone switched on, a short tone too (at most one per ALERT_TONE.minGapMs).
 */
export function useCriticalToasts() {
  const toast = useToast();
  const nav = useNavigate();
  const last = useRef(0);
  const waiting = useRef<AlertEvent[]>([]);
  const timer = useRef(0);
  const seen = useRef(new Set<number>());
  useEffect(
    () => () => {
      window.clearTimeout(timer.current);
      timer.current = 0;
    },
    [],
  );

  const show = (alerts: AlertEvent[]) => {
    last.current = Date.now();
    if (alerts.length === 1) {
      const a = alerts[0];
      toast(T.critical(a.title ?? ALERT_TYPE_TEXT[a.type] ?? a.type), false, { tone: 'critical', action: { label: T.view, onClick: () => nav(ROUTES.alert(a.id)) } });
    } else toast(T.criticalMany(num(alerts.length)), false, { tone: 'critical', action: { label: T.view, onClick: () => nav(ROUTES.alerts) } });
  };
  const flush = () => {
    const batch = waiting.current;
    waiting.current = [];
    if (batch.length) show(batch);
  };

  useStream(['alert'], (_t, a) => {
    if (a.severity !== 'critical' || a.resolvedAt || a.ackedAt || seen.current.has(a.id)) return;
    seen.current.add(a.id);
    // the ids only guard against the same alert arriving twice: the oldest are forgotten
    if (seen.current.size > SHOWN.criticalToastMemory) seen.current.delete(seen.current.values().next().value as number);
    // optional tone (off by default), at most one per ALERT_TONE.minGapMs
    if (alertSoundOn()) playAlertTone();
    const wait = last.current + CRITICAL_TOAST_THROTTLE_MS - Date.now();
    if (wait <= 0 && !waiting.current.length) return show([a]);
    waiting.current.push(a);
    if (!timer.current) {
      timer.current = window.setTimeout(() => {
        timer.current = 0;
        flush();
      }, Math.max(0, wait));
    }
  });
}
